import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, ViewEncapsulation, ChangeDetectionStrategy, Component, DestroyRef, booleanAttribute, Input, Directive, ContentChild, NgModule } from '@angular/core';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, NgModel, FormControlName, FormControlDirective, NgControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { filter, map, startWith, tap } from 'rxjs/operators';
import { helpMotion } from 'ng-zorro-antd/core/animation';
import { NzFormStatusService } from 'ng-zorro-antd/core/form';
import * as i1 from 'ng-zorro-antd/core/outlet';
import { NzOutletModule } from 'ng-zorro-antd/core/outlet';
import { toBoolean } from 'ng-zorro-antd/core/util';
import { NzI18nService } from 'ng-zorro-antd/i18n';
import { __esDecorate, __runInitializers } from 'tslib';
import { Directionality } from '@angular/cdk/bidi';
import { WithConfig } from 'ng-zorro-antd/core/config';
import * as i2 from 'ng-zorro-antd/icon';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
/** should add nz-row directive to host, track https://github.com/angular/angular/issues/8785 **/
const _c0 = ["*"];
const _c1 = a0 => [a0];
const _c2 = a0 => ({
  $implicit: a0
});
function NzFormControlComponent_Conditional_3_ng_container_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.innerTip);
  }
}
function NzFormControlComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2)(1, "div", 4);
    i0.ɵɵtemplate(2, NzFormControlComponent_Conditional_3_ng_container_2_Template, 2, 1, "ng-container", 5);
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("@helpMotion", undefined);
    i0.ɵɵadvance();
    i0.ɵɵclassMap(i0.ɵɵpureFunction1(5, _c1, "ant-form-item-explain-" + ctx_r0.status));
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.innerTip)("nzStringTemplateOutletContext", i0.ɵɵpureFunction1(7, _c2, ctx_r0.validateControl));
  }
}
function NzFormControlComponent_Conditional_4_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtext(1);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵtextInterpolate(ctx_r0.nzExtra);
  }
}
function NzFormControlComponent_Conditional_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3);
    i0.ɵɵtemplate(1, NzFormControlComponent_Conditional_4_ng_container_1_Template, 2, 1, "ng-container", 6);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r0.nzExtra);
  }
}
function NzFormLabelComponent_Conditional_2_ng_container_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵelement(1, "nz-icon", 2);
    i0.ɵɵelementContainerEnd();
  }
  if (rf & 2) {
    const tooltipIconType_r1 = ctx.$implicit;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzType", tooltipIconType_r1)("nzTheme", ctx_r1.tooltipIcon.theme);
  }
}
function NzFormLabelComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "span", 0);
    i0.ɵɵtemplate(1, NzFormLabelComponent_Conditional_2_ng_container_1_Template, 2, 2, "ng-container", 1);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("nzTooltipTitle", ctx_r1.nzTooltipTitle);
    i0.ɵɵadvance();
    i0.ɵɵproperty("nzStringTemplateOutlet", ctx_r1.tooltipIcon.type);
  }
}
let NzFormItemComponent = /*#__PURE__*/(() => {
  class NzFormItemComponent {
    cdr = inject(ChangeDetectorRef);
    status = '';
    hasFeedback = false;
    withHelpClass = false;
    setWithHelpViaTips(value) {
      this.withHelpClass = value;
      this.cdr.markForCheck();
    }
    setStatus(status) {
      this.status = status;
      this.cdr.markForCheck();
    }
    setHasFeedback(hasFeedback) {
      this.hasFeedback = hasFeedback;
      this.cdr.markForCheck();
    }
    static ɵfac = function NzFormItemComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormItemComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzFormItemComponent,
      selectors: [["nz-form-item"]],
      hostAttrs: [1, "ant-form-item"],
      hostVars: 12,
      hostBindings: function NzFormItemComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("ant-form-item-has-success", ctx.status === "success")("ant-form-item-has-warning", ctx.status === "warning")("ant-form-item-has-error", ctx.status === "error")("ant-form-item-is-validating", ctx.status === "validating")("ant-form-item-has-feedback", ctx.hasFeedback && ctx.status)("ant-form-item-with-help", ctx.withHelpClass);
        }
      },
      exportAs: ["nzFormItem"],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NzFormItemComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzFormItemComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const NZ_CONFIG_MODULE_NAME = 'form';
const DefaultTooltipIcon = {
  type: 'question-circle',
  theme: 'outline'
};
let NzFormDirective = (() => {
  let _nzNoColon_decorators;
  let _nzNoColon_initializers = [];
  let _nzNoColon_extraInitializers = [];
  let _nzAutoTips_decorators;
  let _nzAutoTips_initializers = [];
  let _nzAutoTips_extraInitializers = [];
  let _nzTooltipIcon_decorators;
  let _nzTooltipIcon_initializers = [];
  let _nzTooltipIcon_extraInitializers = [];
  let _nzLabelWrap_decorators;
  let _nzLabelWrap_initializers = [];
  let _nzLabelWrap_extraInitializers = [];
  return class NzFormDirective {
    static {
      const _metadata = typeof Symbol === "function" && Symbol.metadata ? Object.create(null) : void 0;
      _nzNoColon_decorators = [WithConfig()];
      _nzAutoTips_decorators = [WithConfig()];
      _nzTooltipIcon_decorators = [WithConfig()];
      _nzLabelWrap_decorators = [WithConfig()];
      __esDecorate(null, null, _nzNoColon_decorators, {
        kind: "field",
        name: "nzNoColon",
        static: false,
        private: false,
        access: {
          has: obj => "nzNoColon" in obj,
          get: obj => obj.nzNoColon,
          set: (obj, value) => {
            obj.nzNoColon = value;
          }
        },
        metadata: _metadata
      }, _nzNoColon_initializers, _nzNoColon_extraInitializers);
      __esDecorate(null, null, _nzAutoTips_decorators, {
        kind: "field",
        name: "nzAutoTips",
        static: false,
        private: false,
        access: {
          has: obj => "nzAutoTips" in obj,
          get: obj => obj.nzAutoTips,
          set: (obj, value) => {
            obj.nzAutoTips = value;
          }
        },
        metadata: _metadata
      }, _nzAutoTips_initializers, _nzAutoTips_extraInitializers);
      __esDecorate(null, null, _nzTooltipIcon_decorators, {
        kind: "field",
        name: "nzTooltipIcon",
        static: false,
        private: false,
        access: {
          has: obj => "nzTooltipIcon" in obj,
          get: obj => obj.nzTooltipIcon,
          set: (obj, value) => {
            obj.nzTooltipIcon = value;
          }
        },
        metadata: _metadata
      }, _nzTooltipIcon_initializers, _nzTooltipIcon_extraInitializers);
      __esDecorate(null, null, _nzLabelWrap_decorators, {
        kind: "field",
        name: "nzLabelWrap",
        static: false,
        private: false,
        access: {
          has: obj => "nzLabelWrap" in obj,
          get: obj => obj.nzLabelWrap,
          set: (obj, value) => {
            obj.nzLabelWrap = value;
          }
        },
        metadata: _metadata
      }, _nzLabelWrap_initializers, _nzLabelWrap_extraInitializers);
      if (_metadata) Object.defineProperty(this, Symbol.metadata, {
        enumerable: true,
        configurable: true,
        writable: true,
        value: _metadata
      });
    }
    destroyRef = inject(DestroyRef);
    directionality = inject(Directionality);
    _nzModuleName = NZ_CONFIG_MODULE_NAME;
    nzLayout = 'horizontal';
    nzNoColon = __runInitializers(this, _nzNoColon_initializers, false);
    nzAutoTips = (__runInitializers(this, _nzNoColon_extraInitializers), __runInitializers(this, _nzAutoTips_initializers, {}));
    nzDisableAutoTips = (__runInitializers(this, _nzAutoTips_extraInitializers), false);
    nzTooltipIcon = __runInitializers(this, _nzTooltipIcon_initializers, DefaultTooltipIcon);
    nzLabelAlign = (__runInitializers(this, _nzTooltipIcon_extraInitializers), 'right');
    nzLabelWrap = __runInitializers(this, _nzLabelWrap_initializers, false);
    dir = (__runInitializers(this, _nzLabelWrap_extraInitializers), 'ltr');
    inputChanges$ = new Subject();
    getInputObservable(changeType) {
      return this.inputChanges$.pipe(filter(changes => changeType in changes), map(value => value[changeType]));
    }
    constructor() {
      this.dir = this.directionality.value;
      this.directionality.change?.pipe(takeUntilDestroyed()).subscribe(direction => {
        this.dir = direction;
      });
      this.destroyRef.onDestroy(() => {
        this.inputChanges$.complete();
      });
    }
    ngOnChanges(changes) {
      this.inputChanges$.next(changes);
    }
    static ɵfac = function NzFormDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormDirective)();
    };
    static ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NzFormDirective,
      selectors: [["", "nz-form", ""]],
      hostAttrs: [1, "ant-form"],
      hostVars: 8,
      hostBindings: function NzFormDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("ant-form-horizontal", ctx.nzLayout === "horizontal")("ant-form-vertical", ctx.nzLayout === "vertical")("ant-form-inline", ctx.nzLayout === "inline")("ant-form-rtl", ctx.dir === "rtl");
        }
      },
      inputs: {
        nzLayout: "nzLayout",
        nzNoColon: [2, "nzNoColon", "nzNoColon", booleanAttribute],
        nzAutoTips: "nzAutoTips",
        nzDisableAutoTips: [2, "nzDisableAutoTips", "nzDisableAutoTips", booleanAttribute],
        nzTooltipIcon: "nzTooltipIcon",
        nzLabelAlign: "nzLabelAlign",
        nzLabelWrap: [2, "nzLabelWrap", "nzLabelWrap", booleanAttribute]
      },
      exportAs: ["nzForm"],
      features: [i0.ɵɵNgOnChangesFeature]
    });
  };
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzFormControlComponent = /*#__PURE__*/(() => {
  class NzFormControlComponent {
    cdr = inject(ChangeDetectorRef);
    i18n = inject(NzI18nService);
    nzFormStatusService = inject(NzFormStatusService);
    destroyRef = inject(DestroyRef);
    _hasFeedback = false;
    validateChanges = Subscription.EMPTY;
    validateString = null;
    localeId;
    autoErrorTip;
    get disableAutoTips() {
      return this.nzDisableAutoTips !== undefined ? toBoolean(this.nzDisableAutoTips) : !!this.nzFormDirective?.nzDisableAutoTips;
    }
    status = '';
    validateControl = null;
    innerTip = null;
    defaultValidateControl;
    nzSuccessTip;
    nzWarningTip;
    nzErrorTip;
    nzValidatingTip;
    nzExtra;
    nzAutoTips = {};
    nzDisableAutoTips;
    set nzHasFeedback(value) {
      this._hasFeedback = value;
      this.nzFormStatusService.formStatusChanges.next({
        status: this.status,
        hasFeedback: this._hasFeedback
      });
      if (this.nzFormItemComponent) {
        this.nzFormItemComponent.setHasFeedback(this._hasFeedback);
      }
    }
    get nzHasFeedback() {
      return this._hasFeedback;
    }
    set nzValidateStatus(value) {
      if (value instanceof AbstractControl || value instanceof NgModel) {
        this.validateControl = value;
        this.validateString = null;
        this.watchControl();
      } else if (value instanceof FormControlName) {
        this.validateControl = value.control;
        this.validateString = null;
        this.watchControl();
      } else {
        this.validateString = value;
        this.validateControl = null;
        this.setStatus();
      }
    }
    watchControl() {
      this.validateChanges.unsubscribe();
      /** miss detect https://github.com/angular/angular/issues/10887 **/
      if (this.validateControl && this.validateControl.statusChanges) {
        this.validateChanges = this.validateControl.statusChanges.pipe(startWith(null), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          if (!this.disableAutoTips) {
            this.updateAutoErrorTip();
          }
          this.setStatus();
          this.cdr.markForCheck();
        });
      }
    }
    setStatus() {
      this.status = this.getControlStatus(this.validateString);
      this.innerTip = this.getInnerTip(this.status);
      this.nzFormStatusService.formStatusChanges.next({
        status: this.status,
        hasFeedback: this.nzHasFeedback
      });
      if (this.nzFormItemComponent) {
        this.nzFormItemComponent.setWithHelpViaTips(!!this.innerTip);
        this.nzFormItemComponent.setStatus(this.status);
      }
    }
    getControlStatus(validateString) {
      let status;
      if (validateString === 'warning' || this.validateControlStatus('INVALID', 'warning')) {
        status = 'warning';
      } else if (validateString === 'error' || this.validateControlStatus('INVALID')) {
        status = 'error';
      } else if (validateString === 'validating' || validateString === 'pending' || this.validateControlStatus('PENDING')) {
        status = 'validating';
      } else if (validateString === 'success' || this.validateControlStatus('VALID')) {
        status = 'success';
      } else {
        status = '';
      }
      return status;
    }
    validateControlStatus(validStatus, statusType) {
      if (!this.validateControl) {
        return false;
      } else {
        const {
          dirty,
          touched,
          status
        } = this.validateControl;
        return (!!dirty || !!touched) && (statusType ? this.validateControl.hasError(statusType) : status === validStatus);
      }
    }
    getInnerTip(status) {
      switch (status) {
        case 'error':
          return !this.disableAutoTips && this.autoErrorTip || this.nzErrorTip || null;
        case 'validating':
          return this.nzValidatingTip || null;
        case 'success':
          return this.nzSuccessTip || null;
        case 'warning':
          return this.nzWarningTip || null;
        default:
          return null;
      }
    }
    updateAutoErrorTip() {
      if (this.validateControl) {
        const errors = this.validateControl.errors || {};
        let autoErrorTip = '';
        for (const key in errors) {
          if (errors.hasOwnProperty(key)) {
            autoErrorTip = errors[key]?.[this.localeId] ?? this.nzAutoTips?.[this.localeId]?.[key] ?? this.nzAutoTips.default?.[key] ?? this.nzFormDirective?.nzAutoTips?.[this.localeId]?.[key] ?? this.nzFormDirective?.nzAutoTips.default?.[key];
          }
          if (autoErrorTip) {
            break;
          }
        }
        this.autoErrorTip = autoErrorTip;
      }
    }
    subscribeAutoTips(observable) {
      observable?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (!this.disableAutoTips) {
          this.updateAutoErrorTip();
          this.setStatus();
          this.cdr.markForCheck();
        }
      });
    }
    nzFormItemComponent = inject(NzFormItemComponent, {
      host: true,
      optional: true
    });
    nzFormDirective = inject(NzFormDirective, {
      optional: true
    });
    constructor() {
      this.subscribeAutoTips(this.i18n.localeChange.pipe(tap(locale => this.localeId = locale.locale)));
      this.subscribeAutoTips(this.nzFormDirective?.getInputObservable('nzAutoTips'));
      this.subscribeAutoTips(this.nzFormDirective?.getInputObservable('nzDisableAutoTips').pipe(filter(() => this.nzDisableAutoTips === undefined)));
    }
    ngOnChanges(changes) {
      const {
        nzDisableAutoTips,
        nzAutoTips,
        nzSuccessTip,
        nzWarningTip,
        nzErrorTip,
        nzValidatingTip
      } = changes;
      if (nzDisableAutoTips || nzAutoTips) {
        this.updateAutoErrorTip();
        this.setStatus();
      } else if (nzSuccessTip || nzWarningTip || nzErrorTip || nzValidatingTip) {
        this.setStatus();
      }
    }
    ngOnInit() {
      this.setStatus();
    }
    ngAfterContentInit() {
      if (!this.validateControl && !this.validateString) {
        if (this.defaultValidateControl instanceof FormControlDirective) {
          this.nzValidateStatus = this.defaultValidateControl.control;
        } else {
          this.nzValidateStatus = this.defaultValidateControl;
        }
      }
    }
    static ɵfac = function NzFormControlComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormControlComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzFormControlComponent,
      selectors: [["nz-form-control"]],
      contentQueries: function NzFormControlComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NgControl, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.defaultValidateControl = _t.first);
        }
      },
      hostAttrs: [1, "ant-form-item-control"],
      inputs: {
        nzSuccessTip: "nzSuccessTip",
        nzWarningTip: "nzWarningTip",
        nzErrorTip: "nzErrorTip",
        nzValidatingTip: "nzValidatingTip",
        nzExtra: "nzExtra",
        nzAutoTips: "nzAutoTips",
        nzDisableAutoTips: [2, "nzDisableAutoTips", "nzDisableAutoTips", booleanAttribute],
        nzHasFeedback: [2, "nzHasFeedback", "nzHasFeedback", booleanAttribute],
        nzValidateStatus: "nzValidateStatus"
      },
      exportAs: ["nzFormControl"],
      features: [i0.ɵɵProvidersFeature([NzFormStatusService]), i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c0,
      decls: 5,
      vars: 2,
      consts: [[1, "ant-form-item-control-input"], [1, "ant-form-item-control-input-content"], [1, "ant-form-item-explain", "ant-form-item-explain-connected"], [1, "ant-form-item-extra"], ["role", "alert"], [4, "nzStringTemplateOutlet", "nzStringTemplateOutletContext"], [4, "nzStringTemplateOutlet"]],
      template: function NzFormControlComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 0)(1, "div", 1);
          i0.ɵɵprojection(2);
          i0.ɵɵelementEnd()();
          i0.ɵɵconditionalCreate(3, NzFormControlComponent_Conditional_3_Template, 3, 9, "div", 2);
          i0.ɵɵconditionalCreate(4, NzFormControlComponent_Conditional_4_Template, 2, 1, "div", 3);
        }
        if (rf & 2) {
          i0.ɵɵadvance(3);
          i0.ɵɵconditional(ctx.innerTip ? 3 : -1);
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx.nzExtra ? 4 : -1);
        }
      },
      dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective],
      encapsulation: 2,
      data: {
        animation: [helpMotion]
      },
      changeDetection: 0
    });
  }
  return NzFormControlComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
function toTooltipIcon(value) {
  const icon = typeof value === 'string' ? {
    type: value
  } : value;
  return {
    ...DefaultTooltipIcon,
    ...icon
  };
}
let NzFormLabelComponent = /*#__PURE__*/(() => {
  class NzFormLabelComponent {
    cdr = inject(ChangeDetectorRef);
    nzFor;
    nzRequired = false;
    set nzNoColon(value) {
      this.noColon = value;
    }
    get nzNoColon() {
      return this.noColon !== 'default' ? this.noColon : !!this.nzFormDirective?.nzNoColon;
    }
    noColon = 'default';
    nzTooltipTitle;
    set nzTooltipIcon(value) {
      this._tooltipIcon = toTooltipIcon(value);
    }
    // due to 'get' and 'set' accessor must have the same type, so it was renamed to `tooltipIcon`
    get tooltipIcon() {
      return this._tooltipIcon !== 'default' ? this._tooltipIcon : toTooltipIcon(this.nzFormDirective?.nzTooltipIcon || DefaultTooltipIcon);
    }
    _tooltipIcon = 'default';
    set nzLabelAlign(value) {
      this.labelAlign = value;
    }
    get nzLabelAlign() {
      return this.labelAlign !== 'default' ? this.labelAlign : this.nzFormDirective?.nzLabelAlign || 'right';
    }
    labelAlign = 'default';
    set nzLabelWrap(value) {
      this.labelWrap = value;
    }
    get nzLabelWrap() {
      return this.labelWrap !== 'default' ? this.labelWrap : !!this.nzFormDirective?.nzLabelWrap;
    }
    labelWrap = 'default';
    nzFormDirective = inject(NzFormDirective, {
      skipSelf: true,
      optional: true
    });
    constructor() {
      if (this.nzFormDirective) {
        this.nzFormDirective.getInputObservable('nzNoColon').pipe(filter(() => this.noColon === 'default'), takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
        this.nzFormDirective.getInputObservable('nzTooltipIcon').pipe(filter(() => this._tooltipIcon === 'default'), takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
        this.nzFormDirective.getInputObservable('nzLabelAlign').pipe(filter(() => this.labelAlign === 'default'), takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
        this.nzFormDirective.getInputObservable('nzLabelWrap').pipe(filter(() => this.labelWrap === 'default'), takeUntilDestroyed()).subscribe(() => this.cdr.markForCheck());
      }
    }
    static ɵfac = function NzFormLabelComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormLabelComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzFormLabelComponent,
      selectors: [["nz-form-label"]],
      hostAttrs: [1, "ant-form-item-label"],
      hostVars: 4,
      hostBindings: function NzFormLabelComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("ant-form-item-label-left", ctx.nzLabelAlign === "left")("ant-form-item-label-wrap", ctx.nzLabelWrap);
        }
      },
      inputs: {
        nzFor: "nzFor",
        nzRequired: [2, "nzRequired", "nzRequired", booleanAttribute],
        nzNoColon: [2, "nzNoColon", "nzNoColon", booleanAttribute],
        nzTooltipTitle: "nzTooltipTitle",
        nzTooltipIcon: "nzTooltipIcon",
        nzLabelAlign: "nzLabelAlign",
        nzLabelWrap: [2, "nzLabelWrap", "nzLabelWrap", booleanAttribute]
      },
      exportAs: ["nzFormLabel"],
      ngContentSelectors: _c0,
      decls: 3,
      vars: 6,
      consts: [["nz-tooltip", "", 1, "ant-form-item-tooltip", 3, "nzTooltipTitle"], [4, "nzStringTemplateOutlet"], [3, "nzType", "nzTheme"]],
      template: function NzFormLabelComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "label");
          i0.ɵɵprojection(1);
          i0.ɵɵconditionalCreate(2, NzFormLabelComponent_Conditional_2_Template, 2, 2, "span", 0);
          i0.ɵɵelementEnd();
        }
        if (rf & 2) {
          i0.ɵɵclassProp("ant-form-item-no-colon", ctx.nzNoColon)("ant-form-item-required", ctx.nzRequired);
          i0.ɵɵattribute("for", ctx.nzFor);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.nzTooltipTitle ? 2 : -1);
        }
      },
      dependencies: [NzOutletModule, i1.NzStringTemplateOutletDirective, NzTooltipDirective, NzIconModule, i2.NzIconDirective],
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzFormLabelComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzFormSplitComponent = /*#__PURE__*/(() => {
  class NzFormSplitComponent {
    static ɵfac = function NzFormSplitComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormSplitComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzFormSplitComponent,
      selectors: [["nz-form-split"]],
      hostAttrs: [1, "ant-form-split"],
      exportAs: ["nzFormSplit"],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NzFormSplitComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzFormSplitComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzFormTextComponent = /*#__PURE__*/(() => {
  class NzFormTextComponent {
    static ɵfac = function NzFormTextComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormTextComponent)();
    };
    static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NzFormTextComponent,
      selectors: [["nz-form-text"]],
      hostAttrs: [1, "ant-form-text"],
      exportAs: ["nzFormText"],
      ngContentSelectors: _c0,
      decls: 1,
      vars: 0,
      template: function NzFormTextComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
  return NzFormTextComponent;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */
let NzFormModule = /*#__PURE__*/(() => {
  class NzFormModule {
    static ɵfac = function NzFormModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NzFormModule)();
    };
    static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NzFormModule
    });
    static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [NzFormLabelComponent, NzFormControlComponent, NzGridModule]
    });
  }
  return NzFormModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://github.com/NG-ZORRO/ng-zorro-antd/blob/master/LICENSE
 */

/**
 * Generated bundle index. Do not edit.
 */

export { DefaultTooltipIcon, NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent, NzFormModule, NzFormSplitComponent, NzFormTextComponent };
